import { LMECompanySettings } from "@mcleod/general";
import { ChangeEvent, Checkbox, DataSourceExecutionEvent,  ClickEvent, Layout, SlideoutDecorator } from "@mcleod/components";
import { PartnerXCmp } from "./PartnerXCmp";
import { AutogenLayoutBillingProfileCompareOptions } from "./autogen/AutogenLayoutBillingProfileCompareOptions";
import { Api } from "@mcleod/core";

export class BillingProfileCompareOptions extends AutogenLayoutBillingProfileCompareOptions {

    private _isBrokerageLtlCompany: boolean = false;

    private _isAssetLtl: boolean = false;

    /** This is an event handler for the onChange event of checkboxOrderChange. */
    checkboxOrderChangeOnChange(event: ChangeEvent) {
        const checkBox = event.target as Checkbox;
        this.buttonOrderFields.enabled = checkBox.checked;
    }

    /** This is an event handler for the onChange event of checkboxStopChange. */
    checkboxStopChangeOnChange(event: ChangeEvent) {
        const checkBox = event.target as Checkbox;
        this.buttonStopFields.enabled = checkBox.checked;
    }

    /** This is an event handler for the onChange event of checkboxRefNoChange. */
    checkboxRefNoChangeOnChange(event: ChangeEvent) {
        const checkBox = event.target as Checkbox;
        this.buttonRefNoFields.enabled = checkBox.checked;
    }

    /** This is an event handler for the onChange event of checkboxOCChange. */
    checkboxOCChangeOnChange(event: ChangeEvent) {
        const checkBox = event.target as Checkbox;
        this.buttonOCFields.enabled = checkBox.checked;
    }

    /** This is an event handler for the onChange event of checkboxOCAdd. */
    checkboxOCAddOnChange(event: ChangeEvent) {
        const checkBox = event.target as Checkbox;
        this.panelPassThrough.visible = checkBox.checked;
    }

    /** This is an event handler for the onChange event of checkboxFreightItemChange. */
    checkboxFreightItemChangeOnChange(event: ChangeEvent) {
        const checkBox = event.target as Checkbox;
        this.buttonFreightItemFields.enabled = checkBox.checked;
    }

    /** This is an event handler for the onChange event of checkboxHandlingReqChg. */
    checkboxHandlingReqChgOnChange(event: ChangeEvent) {
        const checkBox = event.target as Checkbox;
        this.buttonHandlingReqFields.enabled = checkBox.checked;
    }

    /** This is an event handler for the onChange event of checkboxMovementChange. */
    checkboxMovementChangeOnChange(event: ChangeEvent) {
        const checkBox = event.target as Checkbox;
        this.buttonMovementFields.enabled = checkBox.checked;
    }

    /** This is an event handler for the onChange event of checkboxExtraPayChange. */
    checkboxExtraPayChangeOnChange(event: ChangeEvent) {
        const checkBox = event.target as Checkbox;
        this.buttonExtraPayFields.enabled = checkBox.checked;
    }

    /** This is an event handler for the afterExecution event of sourceEdiBillingProfileDisplay. */
    sourceEdiBillingProfileDisplayAfterExecution(event: DataSourceExecutionEvent) {
        this.setCompanySettings();
        const isAutoCompare = this.mainDataSource.activeRow?.get("auto_compare") === "Y";
        this.textboxAutoCompareRt.enabled = isAutoCompare;
        this.checkboxUpdateDatabase.enabled = isAutoCompare;
    }

    setCompanySettings() {
        const companySettings = LMECompanySettings.getSingleton();
        this.isAssetLtl = companySettings.isAssetLtl();
        this.isBrokerageLtlCompany = companySettings.isBrokerageLtl();
        const isLtl = this.isAssetLtl || this.isBrokerageLtlCompany;
        this.labelHandlingReq.visible = isLtl;
        this.labelFreightItem.visible = isLtl;
        this.checkboxFreightItemAdd.visible = isLtl;
        this.checkboxFreightItemDelete.visible = isLtl;
        this.checkboxFreightItemChange.visible = isLtl;
        this.checkboxHandlingReqAdd.visible = isLtl;
        this.checkboxHandlingReqDelete.visible = isLtl;
        this.checkboxHandlingReqChg.visible = isLtl;
        this.buttonFreightItemFields.visible = isLtl;
        this.buttonHandlingReqFields.visible = isLtl;
    }
    public get isAssetLtl(): boolean {
        return this._isAssetLtl;
    }
    public set isAssetLtl(value: boolean) {
        this._isAssetLtl = value;
    }
    public get isBrokerageLtlCompany(): boolean {
        return this._isBrokerageLtlCompany;
    }
    public set isBrokerageLtlCompany(value: boolean) {
        this._isBrokerageLtlCompany = value;
    }

    /** Generic method to show PartnerXCmp slideout with compare options */
    private showPartnerCompareSlideout(filter: any, title: string, partnerId: string, version: string, transSet: string, tableName: string) {
        // Open the PartnerXCmp layout as a slideout
        const layout = Layout.getLayout("lme/datafusion/PartnerXCmp") as PartnerXCmp;

        new SlideoutDecorator({
            layout: layout,
            title: title,
            width: "60%",
            fillVerticalSpace: true,
            displayOverlay: true,
            overlayProps: { closeOnClickOff: false },
            addlComponents: [layout.saveButton],
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                await Api.post("lme/datafusion/load-partner-xcmp-data", {transaction_set: transSet, table_name: tableName, partner_id: partnerId, version: version});
                // Set the datasource rows to the model rows we retrieved from the API
                await layout.mainDataSource.search(filter);

                // Filter out duplicate edi_xcompare_id entries - keep only the first occurrence
                const seenIds = new Set<string>();
                const filteredData = layout.sourceEdipartnerXcmp.data.filter(row => {
                    const ediXCompareId = row.get("edi_xcompare_id");
                    if (seenIds.has(ediXCompareId)) {
                        return false; // Skip duplicate
                    }
                    seenIds.add(ediXCompareId);
                    return true; // Keep first occurrence
                });

                // Sort the filtered data alphabetically by caption
                filteredData.sort((a, b) => {
                    const captionA = a.get("edi_xcompare.caption") || "";
                    const captionB = b.get("edi_xcompare.caption") || "";
                    return captionA.localeCompare(captionB);
                });

                // Replace the data source with filtered and sorted results
                layout.sourceEdipartnerXcmp.data = filteredData;
                layout.saveButton.enabled = true;
                layout.setColumnsForInvoice();
                layout.mainDataSource.displayDataInBoundComponents();
                layout.saveButton.addClickListener(async () => {
                    layout.sourceEdipartnerXcmp.data.forEach(row => {
                        // this.sourceEdipartnerXcmp.data.push(row);
                        // const newrow = new ModelRow("lme/datafusion/edipartner_xcmp", false, row.data);
                        if(row.hasChanged()) {
                              row.post();
                        }
                     });
                    // Close the slideout
                    decorator.slideOut();
                 });
            }
        });
    }

/** This is an event handler for the onClick event of buttonOrderFields. */
  async buttonOrderFieldsOnClick(event: ClickEvent) {
    const partnerId = this.mainDataSource.activeRow?.get("partner_id");
    const version  = this.mainDataSource.activeRow?.get("version");
    const transSet = "210";
    const tableName = "edi_billing";

    const filter =  {
         "partner_id": partnerId,
         "version": version,
         "trans_set": transSet,
         "edi_xcompare.source_tablename": tableName,
         "edi_xcompare.tablename": "orders"
     };

    this.showPartnerCompareSlideout(filter, "Order Field Options", partnerId, version, transSet, "orders");
  }

  /** This is an event handler for the onClick event of buttonStopFields. */
  async buttonStopFieldsOnClick(event: ClickEvent) {
    const partnerId = this.mainDataSource.activeRow?.get("partner_id");
    const version  = this.mainDataSource.activeRow?.get("version");
    const transSet = "210";
    const tableName = "edi_stop";

    const filter =  {
         "partner_id": partnerId,
         "version": version,
         "trans_set": transSet,
         "edi_xcompare.source_tablename": tableName
     };

    this.showPartnerCompareSlideout(filter, "Stop Fields Compare Options", partnerId, version, transSet, "stop");
  }

  /** This is an event handler for the onClick event of buttonRefNoFields. */
  async buttonRefNoFieldsOnClick(event: ClickEvent) {
    const partnerId = this.mainDataSource.activeRow?.get("partner_id");
    const version  = this.mainDataSource.activeRow?.get("version");
    const transSet = "210";
    const tableName = "edi_ref_number";

    const filter =  {
         "partner_id": partnerId,
         "version": version,
         "trans_set": transSet,
         "edi_xcompare.source_tablename": tableName
     };

    this.showPartnerCompareSlideout(filter, "Reference Number Fields Compare Options", partnerId, version, transSet, "reference_number");
  }

  /** This is an event handler for the onClick event of buttonOCFields. */
  async buttonOCFieldsOnClick(event: ClickEvent) {
    const partnerId = this.mainDataSource.activeRow?.get("partner_id");
    const version  = this.mainDataSource.activeRow?.get("version");
    const transSet = "210";
    const tableName = "edi_driverextrapay";
    const target = "other_charge";

    const filter =  {
         "partner_id": partnerId,
         "version": version,
         "trans_set": transSet,
         "edi_xcompare.source_tablename": tableName,
         "edi_xcompare.tablename": target
     };

    this.showPartnerCompareSlideout(filter, "Order Charge Fields Compare Options", partnerId, version, transSet, "other_charge");
  }

  /** This is an event handler for the onClick event of buttonFrieghtItemFields. */
  async buttonFrieghtItemFieldsOnClick(event: ClickEvent) {
    const partnerId = this.mainDataSource.activeRow?.get("partner_id");
    const version  = this.mainDataSource.activeRow?.get("version");
    const transSet = "210";
    const tableName = "edi_frt_grp_item";

    const filter =  {
         "partner_id": partnerId,
         "version": version,
         "trans_set": transSet,
         "edi_xcompare.source_tablename": tableName
     };

    this.showPartnerCompareSlideout(filter, "Freight Item Fields Compare Options", partnerId, version, transSet, "freight_group_item");
  }

  /** This is an event handler for the onClick event of buttonHandlingReqFields. */
  async buttonHandlingReqFieldsOnClick(event: ClickEvent) {
    const partnerId = this.mainDataSource.activeRow?.get("partner_id");
    const version  = this.mainDataSource.activeRow?.get("version");
    const transSet = "210";
    const tableName = "edi_hdr_x_fgp";

    const filter =  {
         "partner_id": partnerId,
         "version": version,
         "trans_set": transSet,
         "edi_xcompare.source_tablename": tableName
     };

    this.showPartnerCompareSlideout(filter, "Handling Requirements Fields Compare Options", partnerId, version, transSet, "hdr_x_fgp");
  }

  /** This is an event handler for the onClick event of buttonMovementFields. */
  async buttonMovementFieldsOnClick(event: ClickEvent) {
    const partnerId = this.mainDataSource.activeRow?.get("partner_id");
    const version  = this.mainDataSource.activeRow?.get("version");
    const transSet = "210";
    const tableName = "edi_billing";
    const target     = "movement";

    const filter =  {
         "partner_id": partnerId,
         "version": version,
         "trans_set": transSet,
         "edi_xcompare.source_tablename": tableName,
         "edi_xcompare.tablename": target
     };

    this.showPartnerCompareSlideout(filter, "Movement Fields Compare Options", partnerId, version, transSet, "movement");
  }

  /** This is an event handler for the onClick event of buttonExtraPayFields. */
  async buttonExtraPayFieldsOnClick(event: ClickEvent) {
    const partnerId = this.mainDataSource.activeRow?.get("partner_id");
    const version  = this.mainDataSource.activeRow?.get("version");
    const transSet = "210";
    const tableName = "edi_driverextrapay";
    const target     = "driver_extra_pay";

    const filter =  {
         "partner_id": partnerId,
         "version": version,
         "trans_set": transSet,
         "edi_xcompare.source_tablename": tableName,
         "edi_xcompare.tablename": target
     };

    this.showPartnerCompareSlideout(filter, "Extra Pay Fields Compare Options", partnerId, version, transSet, "driver_extra_pay");
  }

/** This is an event handler for the onChange event of checkboxAutoCompare. */
  checkboxAutoCompareOnChange(event: ChangeEvent) {
    const check = event.target as Checkbox;
    this.checkboxUpdateDatabase.enabled = check.checked;
    this.textboxAutoCompareRt.enabled = check.checked;
    if (event.userInitiatedChange) {
        this.checkboxUpdateDatabase.checked = check.checked;
    }
  }
}
